import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Bot, Inbox, Megaphone, Zap, BarChart3, Database } from 'lucide-react';
import { FEATURES, SIGNUP_URL } from '../data/mock';
import { Button } from './ui/button';

const ICON_MAP = {
  'ai-chatbot': Bot,
  inbox: Inbox,
  broadcast: Megaphone,
  automation: Zap,
  analytics: BarChart3,
  crm: Database,
};

export default function FeaturesShowcase({ limit }) {
  const items = limit ? FEATURES.slice(0, limit) : FEATURES;
  return (
    <section className="section bg-slate-50/50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="chip mb-4">Powerful Features</p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900">
            Everything You Need to Scale
          </h2>
          <p className="mt-4 text-slate-600">
            All the tools you need to automate conversations, collaborate seamlessly and grow your business.
          </p>
        </div>

        <div className="mt-16 space-y-16">
          {items.map((f, i) => {
            const Icon = ICON_MAP[f.id] || Bot;
            const flip = i % 2 === 1;
            return (
              <div
                key={f.id}
                id={f.id}
                className={`grid md:grid-cols-2 gap-10 items-center ${flip ? 'md:[direction:rtl]' : ''}`}
              >
                <div className="[direction:ltr]">
                  <div className="inline-flex items-center gap-2 chip mb-4">
                    <Icon size={14} /> {f.tagline}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900">{f.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{f.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                        <span className="text-sm">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <a href={SIGNUP_URL} target="_blank" rel="noreferrer" className="inline-block mt-6">
                    <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">
                      Try it Free <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </a>
                </div>
                <div className="[direction:ltr] relative">
                  <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-6 overflow-hidden card-lift">
                    <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-emerald-200/40 blur-3xl" />
                    <FeatureMock id={f.id} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeatureMock({ id }) {
  if (id === 'ai-chatbot') {
    return (
      <div className="relative h-full flex flex-col">
        <div className="bg-white rounded-2xl shadow-md p-4 border border-slate-100">
          <p className="text-xs font-semibold text-slate-500">AI Assistant • Online</p>
          <div className="mt-3 space-y-2">
            <div className="bg-slate-100 text-slate-800 text-sm rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]">What's your return policy?</div>
            <div className="bg-emerald-500 text-white text-sm rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%] ml-auto">
              You can return items within 30 days of delivery for a full refund. Shall I share the return link?
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <Stat label="Resolved" value="94%" />
          <Stat label="Avg Time" value="1.2s" />
          <Stat label="Rating" value="4.8★" />
        </div>
      </div>
    );
  }
  if (id === 'inbox') {
    return (
      <div className="bg-white rounded-2xl shadow-md p-4 border border-slate-100 h-full">
        <p className="text-xs font-semibold text-slate-500 mb-3">Shared Team Inbox</p>
        {['Rahul Sharma', 'Neha Verma', 'Aman Singh'].map((n, i) => (
          <div key={n} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-emerald-50 border-b last:border-0 border-slate-100">
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-sm">
              {n.split(' ').map((p) => p[0]).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{n}</p>
              <p className="text-xs text-slate-500 truncate">{['Hi, interested in your product.', 'Any offers today?', 'Need more details please.'][i]}</p>
            </div>
            <span className="text-[10px] text-slate-400">10:3{i} AM</span>
          </div>
        ))}
      </div>
    );
  }
  if (id === 'broadcast') {
    return (
      <div className="bg-white rounded-2xl shadow-md p-5 border border-slate-100 h-full">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">Diwali Sale Campaign</p>
          <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Live</span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <Stat label="Sent" value="12,480" />
          <Stat label="Delivered" value="99.2%" />
          <Stat label="Read" value="78%" />
        </div>
        <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: '78%' }} />
        </div>
      </div>
    );
  }
  if (id === 'automation') {
    return (
      <div className="bg-white rounded-2xl shadow-md p-5 border border-slate-100 h-full">
        <p className="text-xs font-semibold text-slate-500 mb-3">Workflow: New Lead</p>
        {['Trigger: WhatsApp Message', 'Condition: Contains "price"', 'Action: Send Catalog', 'Action: Notify Sales Team'].map((t, i) => (
          <div key={t} className="flex items-center gap-3 py-2">
            <div className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs grid place-items-center font-bold">{i + 1}</div>
            <p className="text-sm text-slate-800">{t}</p>
          </div>
        ))}
      </div>
    );
  }
  if (id === 'analytics') {
    return (
      <div className="bg-white rounded-2xl shadow-md p-5 border border-slate-100 h-full">
        <p className="text-sm font-semibold text-slate-900">Total Conversations</p>
        <p className="text-3xl font-extrabold text-slate-900 mt-1">24,758 <span className="text-emerald-600 text-sm font-semibold">↑ 18.6%</span></p>
        <div className="mt-4 flex items-end gap-1 h-24">
          {[40, 55, 42, 60, 72, 58, 80, 68, 90, 76, 100, 88].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-emerald-500/80" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl shadow-md p-5 border border-slate-100 h-full grid grid-cols-2 gap-3">
      {['Shopify', 'WooCommerce', 'Zapier', 'Razorpay', 'Google Sheets', 'OpenAI'].map((t) => (
        <div key={t} className="border border-slate-200 rounded-lg p-3 text-center text-sm font-semibold text-slate-700">{t}</div>
      ))}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2">
      <p className="text-sm font-extrabold text-slate-900">{value}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
    </div>
  );
}
